import { useState, useEffect, useCallback } from 'react';
import { toast } from '@/lib/caixaToast';
import { formatBRL, parseBRLInput } from '@caixa/lib/format';
import { MoneyInput } from '@caixa/components/MoneyInput';
import { createMovimentoCaixa } from '@caixa/lib/api';

interface RetiradaEntradaModalProps {
  open: boolean;
  onClose: () => void;
  /** Chamado após registrar movimento (ex.: atualizar relatório se estiver aberto). */
  onSuccess?: () => void;
}

type Tab = 'retirada' | 'entrada';

type TipoRetirada = 'compras' | 'despesa' | 'outros';

export function RetiradaEntradaModal({ open, onClose, onSuccess }: RetiradaEntradaModalProps) {
  const [tab, setTab] = useState<Tab>('retirada');
  const [valor, setValor] = useState('');
  const [tipoRetirada, setTipoRetirada] = useState<TipoRetirada>('compras');
  const [descricaoRetirada, setDescricaoRetirada] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setTab('retirada');
      setValor('');
      setTipoRetirada('compras');
      setDescricaoRetirada('');
      setLoading(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const valorNum = parseBRLInput(valor);
  const precisaDescricao = tab === 'retirada';
  const canSubmit =
    valorNum > 0 && (tab === 'entrada' || !precisaDescricao || descricaoRetirada.trim() !== '');

  const handleSubmit = useCallback(async () => {
    if (!canSubmit) return;
    setLoading(true);
    try {
      await createMovimentoCaixa({
        tipo: tab,
        valor: valorNum,
        tipo_retirada: tab === 'retirada' ? tipoRetirada : undefined,
        motivo: tab === 'retirada' ? descricaoRetirada.trim() : undefined,
      });
      toast.success(tab === 'entrada' ? 'Entrada registrada.' : 'Retirada registrada.');
      onSuccess?.();
      onClose();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Erro ao registrar movimento');
    } finally {
      setLoading(false);
    }
  }, [canSubmit, tab, valorNum, tipoRetirada, descricaoRetirada, onSuccess, onClose]);

  if (!open) return null;

  return (
    <div
      className="caixa-overlay caixa-overlay--55"
      role="dialog"
      aria-modal="true"
      aria-labelledby="retirada-entrada-title"
    >
      <div className="caixa-modal caixa-modal--stack">
        <h2 id="retirada-entrada-title" className="sr-only">
          Retirada / Entrada de caixa
        </h2>
        <div className="caixa-modal-section--head" style={{ gap: "0.5rem" }}>
          <button
            type="button"
            onClick={() => setTab('retirada')}
            className={`caixa-choice flex-1 ${tab === 'retirada' ? 'caixa-choice--on' : ''}`}
          >
            Retirada
          </button>
          <button
            type="button"
            onClick={() => setTab('entrada')}
            className={`caixa-choice flex-1 ${tab === 'entrada' ? 'caixa-choice--on' : ''}`}
          >
            Entrada
          </button>
        </div>
        <div className="caixa-modal-section--body">
          <div className="caixa-field">
            <label htmlFor="mov-valor">Valor (R$)</label>
            <MoneyInput
              id="mov-valor"
              value={valor}
              onChange={setValor}
              currencyPrefix
              inputClassName="px-3 py-[0.65rem]"
              className="caixa-input caixa-input--333 text-lg"
            />
          </div>
          {tab === 'retirada' && (
            <>
              <div className="caixa-field">
                <label htmlFor="mov-tipo-retirada">Tipo da retirada</label>
                <select
                  id="mov-tipo-retirada"
                  value={tipoRetirada}
                  onChange={(e) => setTipoRetirada(e.target.value as TipoRetirada)}
                  className="caixa-input caixa-input--333"
                >
                  <option value="compras">Compras</option>
                  <option value="despesa">Despesa</option>
                  <option value="outros">Outros</option>
                </select>
              </div>
              {precisaDescricao && (
                <div className="caixa-field">
                  <label htmlFor="mov-motivo-despesa">Descrição (obrigatória)</label>
                  <input
                    id="mov-motivo-despesa"
                    type="text"
                    value={descricaoRetirada}
                    onChange={(e) => setDescricaoRetirada(e.target.value)}
                    className="caixa-input caixa-input--333"
                    placeholder="Digite a descrição do lançamento"
                    autoComplete="off"
                  />
                </div>
              )}
            </>
          )}
          {valorNum > 0 && (
            <p className="caixa-modal-sub" style={{ marginBottom: 0 }}>
              {tab === 'entrada' ? 'Será somado ao valor em caixa.' : 'Será subtraído do valor em caixa.'}{' '}
              {formatBRL(valorNum)}
            </p>
          )}
        </div>
        <div className="caixa-modal-section--foot">
          <button
            type="button"
            onClick={onClose}
            className="caixa-btn-outline"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit || loading}
            className="caixa-btn-success"
          >
            {loading ? 'Registrando...' : 'Confirmar'}
          </button>
        </div>
      </div>
    </div>
  );
}
